import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
//
import {
  Autocomplete,
  Button,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DialogSimple from '@/components/dialog/DialogSimple';
import {
  FormProvider,
  RHFDatepicker,
  RHFTextField,
} from '@/components/hook-form';
// api
import { getSettingsData } from '@/features/app/api';
import {
  createProviderCityPrice,
  getProviderCityPrice,
  updateProviderCityPrice,
} from '../api';
import { getVariants } from '@/features/services-management/api';
// hooks + utils
import useResponsive from '@/hooks/useResponsive';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import {
  getServiceVariantName,
  isServiceVariantActive,
  PROVIDER_CITY_PRICE_DEFAULT_VALUES,
  PROVIDER_CITY_PRICE_SCHEMA,
} from '../utils';
// types
import {
  ProviderCityPriceFormValues,
  ProviderCityPricePayload,
} from '../types';
import { Variant } from '@/features/services-management/types';

type ProviderCityPriceDialogProps = {
  isOpen: boolean;
  onClose: VoidFunction;
  currentPriceId?: string;
};

export default function ProviderCityPriceDialog({
  isOpen,
  onClose,
  currentPriceId,
}: ProviderCityPriceDialogProps) {
  const [filteredServiceVariants, setFilteredServiceVariants] = useState<
    Variant[]
  >([]);
  const belowLg = useResponsive('down', 'lg');
  const { errorMsg, successMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();

  const belowLgStackDirection = belowLg ? 'column' : 'row';
  const belowLgSpacing = belowLg ? 2 : 1;

  const methods = useForm<ProviderCityPriceFormValues>({
    defaultValues: PROVIDER_CITY_PRICE_DEFAULT_VALUES,
    resolver: yupResolver(PROVIDER_CITY_PRICE_SCHEMA),
  });

  const {
    control,
    handleSubmit,
    setValue,
    reset: resetForm,
    formState: { isSubmitting, errors },
  } = methods;

  const { service_type_id, start_date } = useWatch({ control });

  const { data: settings, isLoading: isSettingsDataLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettingsData,
    onError: (error) =>
      errorMsg(error, `Something went wrong while fetching settings data`),
    refetchOnWindowFocus: false,
    enabled: isOpen,
  });

  const { data: serviceVariants, isLoading: isServiceVariantsLoading } =
    useQuery({
      queryKey: ['serviceVariants'],
      queryFn: getVariants,
      onError: (error) =>
        errorMsg(error, `Something went wrong while fetching service variants`),
      refetchOnWindowFocus: false,
      enabled: isOpen,
    });

  const activeServiceVariants = (serviceVariants || []).filter(
    isServiceVariantActive
  );

  const { data: currentPrice, isLoading: isCurrentPriceLoading } = useQuery({
    queryKey: ['providerCityPrice', currentPriceId],
    queryFn: () => getProviderCityPrice(String(currentPriceId)),
    onSuccess: (apiResponse) => {
      const serviceTypeId =
        apiResponse.service_variant.service?.id ??
        apiResponse.service_variant.service_id;
      const filteredVariants = activeServiceVariants.filter(
        (variant) => variant.service.id === Number(serviceTypeId)
      );

      filteredVariants
        ? setFilteredServiceVariants(filteredVariants)
        : errorMsg('There was an error while getting service variants ready');

      resetForm({
        city_id: apiResponse.city_id,
        service_type_id: serviceTypeId,
        service_variant_id: apiResponse.service_variant_id,
        price: apiResponse.price,
        sun_bank_holiday_extra: apiResponse.sun_bank_holiday_extra,
        start_date: apiResponse.start_date,
        end_date: apiResponse.end_date || '',
      });
    },
    onError: (error) =>
      errorMsg(error, `Something went wrong while fetching provider price`),
    enabled: isOpen && !!currentPriceId && !!settings && !!serviceVariants,
    refetchOnWindowFocus: false,
  });

  const saveProviderCityPriceMutation = useMutation({
    mutationFn: (payload: ProviderCityPricePayload) =>
      currentPriceId
        ? updateProviderCityPrice(currentPriceId, payload)
        : createProviderCityPrice(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['providerCityPrices']);
      successMsg(
        `Provider price successfully ${currentPriceId ? 'updated' : 'created'}`
      );
      handleCloseDialog();
    },
    onError: (error) =>
      errorMsg(
        error,
        `Something went wrong while ${
          currentPriceId ? 'updating' : 'creating'
        } provider price`
      ),
  });

  const handleServiceTypeChange = (serviceTypeId: string) => {
    setValue('service_type_id', serviceTypeId);
    setValue('service_variant_id', '');
    const filteredVariants = activeServiceVariants.filter(
      (variant) => variant.service.id === Number(serviceTypeId)
    );

    filteredVariants
      ? setFilteredServiceVariants(filteredVariants)
      : errorMsg('There was an error while getting service variants ready');
  };

  const retrieveCityObject = (cityId: number) =>
    settings?.cities.find((city) => city.id === cityId);

  const handleCloseDialog = () => {
    resetForm(PROVIDER_CITY_PRICE_DEFAULT_VALUES);
    setFilteredServiceVariants([]);
    onClose();
  };

  const submitProviderCityPrice = (data: ProviderCityPriceFormValues) => {
    saveProviderCityPriceMutation.mutate({
      city_id: Number(data.city_id),
      service_variant_id: Number(data.service_variant_id),
      price: Number(data.price),
      sun_bank_holiday_extra: Number(data.sun_bank_holiday_extra),
      start_date: new Date(data.start_date).toISOString(),
      end_date: new Date(data.end_date).toISOString(),
    });
  };

  const showSkeleton =
    isSettingsDataLoading ||
    isServiceVariantsLoading ||
    !settings ||
    !serviceVariants ||
    (currentPriceId && isCurrentPriceLoading) ||
    (currentPriceId && !currentPrice);

  return (
    <DialogSimple
      dialogTitle={`${currentPriceId ? 'Edit' : 'Add'} provider price`}
      open={isOpen}
      onClickButtonClose={handleCloseDialog}
      loadingButton={false}
      disagreeButton={false}
      maxWidth="md"
    >
      <FormProvider
        methods={methods}
        onSubmit={handleSubmit(submitProviderCityPrice)}
      >
        {!showSkeleton ? (
          <Stack spacing={belowLg ? 2 : 1.5} sx={{ px: 3, py: 1.5 }}>
            <Controller
              name="city_id"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  fullWidth
                  value={retrieveCityObject(Number(field.value)) ?? null}
                  filterSelectedOptions
                  options={settings.cities || []}
                  noOptionsText="No such city"
                  getOptionLabel={(option) => option.name}
                  onChange={(_event, newValue) =>
                    setValue('city_id', newValue ? Number(newValue.id) : '')
                  }
                  renderOption={(props, city) => (
                    <MenuItem {...props} key={city.id} value={city.id}>
                      {city.name}
                    </MenuItem>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search city"
                      fullWidth
                      autoFocus
                      size="small"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              )}
            />

            <Stack direction={belowLgStackDirection} spacing={belowLgSpacing}>
              <RHFTextField
                select
                name="service_type_id"
                label="Service type"
                onChange={(event) =>
                  handleServiceTypeChange(event.target.value)
                }
                fullWidth
                size="small"
              >
                {settings.services.map((service) => (
                  <MenuItem key={service.id} value={service.id}>
                    {service.name}
                  </MenuItem>
                ))}
              </RHFTextField>

              <RHFTextField
                select
                name="service_variant_id"
                label="Service variant"
                disabled={!service_type_id}
                fullWidth
                size="small"
              >
                {filteredServiceVariants.map((variant) => (
                  <MenuItem key={variant.id} value={variant.id}>
                    {getServiceVariantName(variant)}
                  </MenuItem>
                ))}
              </RHFTextField>
            </Stack>

            <Stack direction={belowLgStackDirection} spacing={belowLgSpacing}>
              <RHFTextField
                name="price"
                label="Price"
                inputProps={{ type: 'number', step: '0.5', min: 0 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">£</InputAdornment>
                  ),
                }}
                fullWidth
                size="small"
              />

              <RHFTextField
                name="sun_bank_holiday_extra"
                label="Sunday/Bank holiday extra"
                inputProps={{ type: 'number', step: '0.5', min: 0 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">£</InputAdornment>
                  ),
                }}
                fullWidth
                size="small"
              />
            </Stack>

            <Stack direction={belowLgStackDirection} spacing={belowLgSpacing}>
              <RHFDatepicker
                name="start_date"
                label="Start date"
                size="small"
                disablePast
              />
              <RHFDatepicker
                name="end_date"
                label="End date"
                size="small"
                disablePast
                minDate={start_date}
                helperTextFromRhfContext={errors.end_date?.message}
              />
            </Stack>

            <Stack
              direction="row"
              justifyContent="flex-end"
              spacing={1.5}
              sx={{ pt: 1.5, pb: 1 }}
            >
              <Button
                variant="outlined"
                size="small"
                color="inherit"
                onClick={handleCloseDialog}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                size="small"
                type="submit"
                loading={
                  isSubmitting || saveProviderCityPriceMutation.isLoading
                }
              >
                {currentPriceId ? 'Save changes' : 'Add price'}
              </LoadingButton>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={1} sx={{ px: 3, pb: 4 }}>
            {[...new Array(5)].map((_, i) => (
              <Skeleton height={40} key={i} />
            ))}
          </Stack>
        )}
      </FormProvider>
    </DialogSimple>
  );
}
